/**
 * Dictation anywhere into a Denker composer. When the user is typing in a
 * Denker input, the anywhere shortcut dictates into that input as its own mic
 * does: words show live and the final replaces them. Nothing is pasted.
 *
 * Each window registers its composers and publishes the one holding keyboard
 * focus as the mirrored `useDictation.focusedComposerId`. The trigger reaches
 * every window: all of them read that one value, so the owner's flow stands
 * aside and only the window holding the composer acts on it.
 */
import { isDictationSettled } from "./dictation-status";
import { DICTATION_ANYWHERE_OWNER_ID } from "./dictation-anywhere/dictation-anywhere-store";
import { useDictation } from "./use-dictation";

export interface DictationComposer {
  /** The composer's DOM holds `node`. */
  contains: (node: Node) => boolean;
  /** It takes new input now; a blocked one leaves the shortcut to anywhere. */
  enabled: boolean;
  /** What its mic button does: start a recording it owns, or stop it. */
  toggle: () => void;
}

const composers = new Map<string, DictationComposer>();
/** What this window last published. */
let published: string | null = null;
let tracking = false;

/** This window's enabled composer holding keyboard focus, while the window
 *  has it. */
function focusedHere(): string | null {
  if (!document.hasFocus()) return null;
  const active = document.activeElement;
  if (!active) return null;
  for (const [id, composer] of composers) {
    if (composer.enabled && composer.contains(active)) return id;
  }
  return null;
}

function publish(next = focusedHere()): void {
  if (next === published) return;
  const previous = published;
  published = next;
  if (next !== null) {
    useDictation.setState({ focusedComposerId: next });
  } else if (useDictation.getState().focusedComposerId === previous) {
    // Only this window's own value: another window may have published since.
    useDictation.setState({ focusedComposerId: null });
  }
}

/** Focus events fire before the new element and window focus settle. */
function schedulePublish(): void {
  setTimeout(publish, 0);
}

function trackFocus(): void {
  if (tracking || typeof document === "undefined") return;
  tracking = true;
  document.addEventListener("focusin", schedulePublish);
  document.addEventListener("focusout", schedulePublish);
  window.addEventListener("focus", schedulePublish);
  window.addEventListener("blur", schedulePublish);
  // A window that reloads or closes must not leave its composer published.
  window.addEventListener("pagehide", () => publish(null));
}

/** Makes `id` reachable by the anywhere shortcut; returns the unregister. */
export function registerDictationComposer(
  id: string,
  composer: DictationComposer,
): () => void {
  trackFocus();
  composers.set(id, composer);
  schedulePublish();
  return () => {
    if (composers.get(id) === composer) composers.delete(id);
    schedulePublish();
  };
}

/**
 * The dictation anywhere trigger, in every window, before the owner's flow:
 * true when a composer takes it — the one typed in starts, or the one whose
 * recording runs stops — and the flow must stand aside. Only the window
 * holding that composer toggles it.
 */
export function routeAnywhereTriggerToComposer(): boolean {
  const { status, ownerId, focusedComposerId } = useDictation.getState();
  if (!isDictationSettled(status)) {
    if (!ownerId || ownerId === DICTATION_ANYWHERE_OWNER_ID) return false;
    composers.get(ownerId)?.toggle();
    return true;
  }
  if (!focusedComposerId) return false;
  if (focusedHere() === focusedComposerId) {
    composers.get(focusedComposerId)?.toggle();
  }
  return true;
}
